import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { finalize } from 'rxjs/operators';
import {
  EnvironmentInfo,
  EnvironmentInfoStatus,
  EnvironmentRestriction,
  EnvironmentInfoService,
  EnvironmentNotice,
  EnvironmentRestrictionMode,
  EnvironmentWorkflow,
  EnvironmentWorkflowStatus
} from '@app/services/environment-info.service';
import { PageShellComponent } from '../../_components/page-shell/page-shell.component';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-environment-info',
  templateUrl: './environment-info.component.html',
  styleUrls: ['./environment-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageShellComponent, DatePipe]
})
export class EnvironmentInfoComponent implements OnInit, OnDestroy {
  private environmentInfoService = inject(EnvironmentInfoService);
  private titleService = inject(Title);

  readonly info = signal<EnvironmentInfo | null>(null);
  readonly loading = signal(false);
  readonly error = signal('');

  private previousTitle = '';

  ngOnInit(): void {
    this.previousTitle = this.titleService.getTitle();
    this.titleService.setTitle('Snöball - Environment Info');
    // Every deployment has a page, production included: the server labels the environment
    // and lists the restrictions that are live there.
    this.load();
  }

  get eyebrow(): string {
    return this.info()?.environment?.production ? 'Production environment' : 'Testing environment';
  }

  ngOnDestroy(): void {
    this.titleService.setTitle(this.previousTitle);
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.environmentInfoService.get()
      .pipe(finalize(() => {
        this.loading.set(false);
      }))
      .subscribe({
        next: (response) => {
          this.info.set(response.data);
        },
        error: () => {
          this.info.set(null);
          this.error.set('Environment information could not be loaded. Please try again.');
        }
      });
  }

  modeLabel(mode: EnvironmentRestrictionMode): string {
    if (mode === 'disabled') {
      return 'Disabled';
    }
    if (mode === 'restricted') {
      return 'Restricted';
    }
    return 'Unrestricted';
  }

  statusLabel(status: EnvironmentWorkflowStatus): string {
    if (status === 'allowed') {
      return 'Allowed';
    }
    if (status === 'caution') {
      return 'Use caution';
    }
    if (status === 'blocked') {
      return 'Blocked';
    }
    return 'Confirm first';
  }

  infoStatusLabel(status: EnvironmentInfoStatus): string {
    if (status === 'disabled' || status === 'restricted' || status === 'unrestricted') {
      return this.modeLabel(status);
    }
    return this.statusLabel(status);
  }

  trackRestriction(_index: number, restriction: EnvironmentRestriction): string {
    return restriction.key;
  }

  trackWorkflow(_index: number, workflow: EnvironmentWorkflow): string {
    return workflow.key;
  }

  trackNotice(index: number, notice: EnvironmentNotice): string {
    return `${index}:${notice.title}`;
  }
}
